import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { ChevronDown, ChevronUp } from 'react-feather';
import { Core, Greyscale } from 'themes/rebrand/colours';
import { journeyFareShape } from 'constants/propTypes';
import {
  CollapseHeader,
  CollapseTitle,
  SelectedFareInfo,
  ExistingFareInfo,
  CollapseIcon,
  StyledCircle,
  StyledArrowRight
} from './JourneySearchTables.styles';

const CollapsibleJourneyHeader = ({
  isExpanded,
  onToggle,
  direction,
  date,
  selectedFare,
  existingTicket,
  currency,
  getCurrencySymbol,
  getClassOfServiceColor
}) => {
  const bgColor = getClassOfServiceColor(selectedFare?.classOfService?.code);
  const hasClassOfService = !!selectedFare?.classOfService?.code;
  const titleColor = hasClassOfService ? Greyscale.White : Greyscale.Charcoal;
  const fareInfoColor = hasClassOfService ? Greyscale.White : Core.CoreBlue;
  const iconColor = hasClassOfService ? Greyscale.White : Greyscale.SteelGrey;

  return (
    <CollapseHeader onClick={onToggle} bgColor={bgColor}>
      <CollapseTitle textColor={titleColor}>
        <div>
          <FormattedMessage id={`searchResults.${direction}`} defaultMessage={direction === 'outbound' ? 'Outbound' : 'Inbound'} />
          {date && ` - ${moment(date, 'YYYY-MM-DD').format('ddd D MMM YYYY')}`}
        </div>

        {existingTicket && (
          <ExistingFareInfo textColor={fareInfoColor}>
            <strong>Existing: </strong>
            {existingTicket.serviceType?.code} {existingTicket.serviceName}
            <StyledCircle size={6} />
            {existingTicket.timing?.departs} <StyledArrowRight size={14} />
            {existingTicket.timing?.arrives}
            <StyledCircle size={6} />
            {existingTicket.classOfService?.name || existingTicket.classOfService?.code}
            {existingTicket.products && existingTicket.products.length > 0 && (
              <>
                <StyledCircle size={6} />
                {getCurrencySymbol(currency)}
                {existingTicket.products.reduce((sum, item) => sum + (item.price || 0), 0).toFixed(2)}
              </>
            )}
          </ExistingFareInfo>
        )}

        {selectedFare && selectedFare.legs && selectedFare.legs.length > 0 && (
          <SelectedFareInfo textColor={fareInfoColor}>
            {existingTicket && <strong>Selected: </strong>}
            {selectedFare.legs[0]?.serviceType?.code} {selectedFare.legs[0]?.serviceName}
            <StyledCircle size={6} />
            {selectedFare.legs[0]?.timing?.departs} <StyledArrowRight size={14} />
            {selectedFare.legs[selectedFare.legs.length - 1]?.timing?.arrives}
            <StyledCircle size={6} />
            {selectedFare.classOfService?.name || selectedFare.classOfService?.code}
            {selectedFare.bookingItems && selectedFare.bookingItems.length > 0 && (
              <>
                <StyledCircle size={6} />
                {getCurrencySymbol(currency)}
                {selectedFare.bookingItems.reduce((sum, item) => sum + (item.price || 0), 0).toFixed(2)}
              </>
            )}
          </SelectedFareInfo>
        )}
      </CollapseTitle>
      <CollapseIcon isExpanded={isExpanded} textColor={iconColor}>
        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </CollapseIcon>
    </CollapseHeader>
  );
};

CollapsibleJourneyHeader.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  direction: PropTypes.oneOf(['outbound', 'inbound']).isRequired,
  date: PropTypes.string,
  selectedFare: journeyFareShape,
  existingTicket: PropTypes.object,
  currency: PropTypes.string,
  getCurrencySymbol: PropTypes.func.isRequired,
  getClassOfServiceColor: PropTypes.func.isRequired
};

export default CollapsibleJourneyHeader;
